/* Trappbutiken – Quick add-drawer (Wubs).
 *
 * Öppnas från [data-wubs-quick-add="<handle>"] på produktkort. Panelen hämtas med Section
 * Rendering API och injiceras; därefter sköter wubs-product.js resten.
 *
 * ⚠️⚠️ Den här filen innehåller MEDVETET ingen variant-, antals- eller köplogik. Panelen bär
 * produktsidans data-hooks, och wubs-product.js har allt det delegerat på `document` – både
 * variantväljaren, antalsstegaren och submit → lägg i korgen → öppna varukorgsdrawern.
 * Skriv inte om det här; håll i stället panelens hooks i synk med produktsidan.
 *
 * KONFIGURATORLÄGET (2026-10-13): `window.WubsKonfigQA.oppna({...})` öppnar SAMMA låda med
 * produktsidans mobilvy (sections/wubs-konfig-qa.liquid). Där lägger knappen inte i korgen –
 * valet lämnas tillbaka till konfiguratorn via `onVal`. Se visaKonfig().
 */
(function () {
  const SEK = 'wubs-quick-add'; // sektionsfilens namn, används som section_id
  const SEK_KONFIG = 'wubs-konfig-qa'; // produktvyn i konfiguratorerna (visaKonfig nedan)

  class WubsQuickAddDrawer extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-wubs-qa-body]');
      this.laddar = this.querySelector('[data-wubs-qa-laddar]');
      this.fel = this.querySelector('[data-wubs-qa-fel]');
      this.cache = new Map();
      this.rubrik = this.querySelector('[data-wubs-qa-rubrik]');
      this.konfig = null; // { onVal, onBort } medan lådan visar en konfigurators produkt
      /* ⚠️ Texterna ligger på drawern som data-kqa-TXT-*. Ett attribut som hette exakt som en
         knapps hook (data-kqa-bort) gjorde att `e.target.closest('[data-kqa-bort]')` träffade
         DRAWERN SJÄLV – closest() räknar med förfäderna – och varje klick i lådan blev "ta bort".
         Uppmätt 2026-10-13: ett klick på "Svart" stängde lådan och tog bort beslaget. */

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-wubs-qa-close]')) { this.close(); return; }
        /* Konfiguratorläget: knappen lämnar valet tillbaka till konfiguratorn i stället för att
           lägga i korgen. Läses ur panelens egna hooks – variantväljaren har redan skrivit vald
           variant i [data-pdp-variant-id], antalsstegaren sitt tal i [data-pdp-qty-input]. */
        const val = e.target.closest('[data-kqa-val]');
        if (val && this.konfig) {
          const panel = val.closest('[data-kqa]');
          const id = Number(panel?.querySelector('[data-pdp-variant-id]')?.value);
          const qty = Math.max(1, parseInt(panel?.querySelector('[data-pdp-qty-input]')?.value, 10) || 1);
          if (!id || val.disabled) return;
          // Valet FÖRST, stängningen sedan: konfiguratorn ritar om sina kort, och close()
          // lämnar tillbaka fokus till det kort som finns EFTER omritningen.
          if (typeof this.konfig.onVal === 'function') this.konfig.onVal({ variantId: id, qty });
          this.close();
          return;
        }
        if (e.target.closest('[data-kqa-bort]') && this.konfig) {
          if (typeof this.konfig.onBort === 'function') this.konfig.onBort();
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });
    }

    open() {
      /* En stängning som ännu inte hunnit gömma lådan (220 ms) får inte gömma den mitt i nästa
         öppning – och en låda som redan är öppen ska inte låsa scrollen en gång till, då hade
         djupräknaren i WubsScroll aldrig kommit tillbaka till noll. */
      clearTimeout(this._stang);
      if (!this.hidden && this.classList.contains('is-open')) return;
      this.hidden = false;
      WubsScroll.las();   // låser <html>, inte <body> – annars tappar sticky sin scrollport
      /* Synkron reflow, inte requestAnimationFrame: rAF throttlas när fliken inte målar och
         panelen fastnar då utanför kanten. Samma lösning som varukorgsdrawern. */
      void this.offsetWidth;
      this.classList.add('is-open');
      this.querySelector('.wubs-qad__close')?.focus();
    }

    close() {
      if (this.hidden || !this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      WubsScroll.slapp();
      // Återlämnas fokus till kortet som öppnade lådan – annars hamnar det på <body> och
      // tangentbordsanvändaren får börja om från toppen av konfiguratorn.
      // `fokus` får vara en funktion: hyllplanet och tröskeln ritar om hela kortrutnätet vid ett
      // val, och elementet som öppnade lådan finns då inte längre.
      let tillbaka = this.konfig?.fokus;
      this.konfig = null;
      if (typeof tillbaka === 'function') tillbaka = tillbaka();
      if (tillbaka && tillbaka.isConnected) tillbaka.focus({ preventScroll: true });
      this._stang = setTimeout(() => {
        this.hidden = true;
      }, 220);
    }

    /* Rubriken och bredden skiljer sig mellan lägena. Sätts vid VARJE öppning, så ett
       produktkort som öppnar lådan efter en konfigurator får tillbaka sin egen rubrik. */
    lage(konfig) {
      this.classList.toggle('wubs-qad--konfig', konfig);
      const txt = konfig ? this.dataset.kqaTxtRubrik : this.dataset.rubrik;
      if (this.rubrik && txt) this.rubrik.textContent = txt;
      this.querySelector('[role="dialog"]')?.setAttribute('aria-label', txt || '');
    }

    /* Produktsidans CSS/JS hämtas vid FÖRSTA öppningen, inte på varje sidladdning.
       Custom-elementet <wubs-variant-picker> uppgraderar redan injicerad markup av sig
       självt när definitionen landar, men vi väntar in skriptet ändå: annars hinner
       panelen visas en tick utan att prisraden är kopplad. */
    assets() {
      if (this._assets) return this._assets;
      const css = this.dataset.pdpCss;
      const js = this.dataset.pdpJs;
      if (css && !document.querySelector(`link[href="${css}"]`)) {
        const l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = css;
        document.head.appendChild(l);
      }
      this._assets = new Promise((res) => {
        if (!js || window.customElements.get('wubs-variant-picker')) return res();
        const s = document.createElement('script');
        s.src = js;
        s.defer = true;
        s.onload = () => res();
        s.onerror = () => res(); // panelen visas ändå, med länk till produktsidan
        document.head.appendChild(s);
      });
      return this._assets;
    }

    /* Konfiguratorpanelens stilmall väntas IN innan panelen visas (till skillnad från
       produktsidans, som snabbköpet klarar sig utan en tick): utan den står galleriets bilder
       ostylade på varandra i full storlek under en bildruta. */
    konfigCss() {
      if (this._kqaCss) return this._kqaCss;
      const href = this.dataset.kqaCss;
      this._kqaCss = new Promise((res) => {
        if (!href || document.querySelector(`link[href="${href}"]`)) return res();
        const l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = href;
        l.onload = () => res();
        l.onerror = () => res();
        document.head.appendChild(l);
      });
      return this._kqaCss;
    }

    /* Hämtar en panel och lägger in den. Delad av snabbköpet och konfiguratorläget. */
    async hamta(url, valj) {
      // Två snabba klick på två kort: bara den SENASTE hämtningen får lägga in sin panel.
      const tur = (this._tur = (this._tur || 0) + 1);
      this.body.querySelector('[data-wubs-quickadd]')?.remove();
      this.body.scrollTop = 0;
      this.fel.hidden = true;
      this.laddar.hidden = false;
      this.body.setAttribute('aria-busy', 'true');
      let html = this.cache.get(url);
      if (!html) {
        const svar = await fetch(url);
        if (!svar.ok) throw new Error(svar.status);
        html = await svar.text();
        this.cache.set(url, html);
      }
      /* Sektionssvaret är omslutet av en <div id="shopify-section-…">. Vi plockar ut
         själva panelen – annars ärver den wrapperns eventuella sektionsmarginaler. */
      if (tur !== this._tur) throw new Error('inaktuell');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const panel = doc.querySelector(valj);
      if (!panel) throw new Error('ingen panel i svaret');
      this.laddar.hidden = true;
      this.body.appendChild(panel);
      this.body.removeAttribute('aria-busy');
      return panel;
    }

    visaFel() {
      this.laddar.hidden = true;
      this.fel.hidden = false;
      this.body.removeAttribute('aria-busy');
    }

    /* KONFIGURATORLÄGET (2026-10-13). Konfiguratorernas beslags-, konsol- och tillbehörskort
       öppnar produkten här med kortets variant och antal förvalda. Knappen lägger INTE i korgen:
       `onVal({ variantId, qty })` anropas och konfiguratorn gör resten (markerar kortet, räknar
       in raden). `vald` = kortet är redan med i konfigurationen → "Uppdatera" + ta bort-länk.
       `fokus` = elementet som får tillbaka fokus när lådan stängs. */
    async visaKonfig({ handle, variantId, qty = 1, vald = false, onVal, onBort, fokus } = {}) {
      if (!handle) return;
      this.lage(true);
      this.open();
      this.konfig = { onVal, onBort, fokus };
      const root = window.Shopify?.routes?.root || '/';
      let url = `${root}products/${encodeURIComponent(handle)}?section_id=${SEK_KONFIG}`;
      if (variantId) url += `&variant=${encodeURIComponent(variantId)}`;
      try {
        await Promise.all([this.assets(), this.konfigCss()]);
        const panel = await this.hamta(url, '[data-kqa]');
        const knapp = panel.querySelector('[data-kqa-val]');
        const etikett = vald ? this.dataset.kqaTxtUppdatera : this.dataset.kqaTxtLagg;
        if (knapp && etikett) {
          // setATC() läser data-label-add vid varje variantbyte – därför skrivs texten där också.
          knapp.dataset.labelAdd = etikett;
          const lbl = knapp.querySelector('[data-pdp-atc-label]');
          if (lbl && !knapp.disabled) lbl.textContent = etikett;
        }
        const antal = panel.querySelector('[data-pdp-qty-input]');
        if (antal) antal.value = String(Math.max(1, parseInt(qty, 10) || 1));
        const bort = panel.querySelector('[data-kqa-bort]');
        if (bort) {
          bort.hidden = !(vald && typeof onBort === 'function');
          if (this.dataset.kqaTxtBort) bort.textContent = this.dataset.kqaTxtBort;
        }
      } catch (err) {
        if (err.message !== 'inaktuell') this.visaFel();
      }
    }

    /* `variantId` = kortets egen variant (2026-11-03). Ett delat kort står för ett material:
       Energilistens "Obehandlad Furu" öppnade panelen på Vitfolierad MDF / Vit. */
    async visa(handle, variantId) {
      this.lage(false);
      this.open();
      try {
        await this.assets();
        const root = window.Shopify?.routes?.root || '/';
        let url = `${root}products/${encodeURIComponent(handle)}?section_id=${SEK}`;
        if (variantId) url += `&variant=${encodeURIComponent(variantId)}`;
        const panel = await this.hamta(url, '.wubs-qa');
        panel.querySelector('[data-option-input]:checked, [data-pdp-atc]')?.focus?.();
      } catch (err) {
        if (err.message !== 'inaktuell') this.visaFel();
      }
    }
  }

  if (!customElements.get('wubs-quick-add-drawer')) {
    customElements.define('wubs-quick-add-drawer', WubsQuickAddDrawer);
  }

  /* Knapparna sitter inne i länkade produktkort – preventDefault krävs, annars följer
     klicket kortlänken till produktsidan innan drawern hinner öppnas. Samma sak som
     den globala ATC-lyssnaren i wubs-cart-drawer.js gör. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wubs-quick-add]');
    if (!btn) return;
    const drawer = document.querySelector('wubs-quick-add-drawer');
    if (!drawer) return; // ingen drawer på sidan → låt kortlänken gå till produktsidan
    e.preventDefault();
    e.stopPropagation();
    drawer.visa(btn.dataset.wubsQuickAdd, btn.dataset.wubsQuickAddVariant);
  });

  /* Konfiguratorernas ingång. Konfiguratorerna ropar hit i stället för att leta upp drawern
     själva, så reserven bor på ett ställe: finns ingen låda på sidan (snabbköpet avstängt i
     temainställningarna) öppnas produktsidan i en ny flik – kunden lämnar inte konfigurationen. */
  window.WubsKonfigQA = {
    oppna(opts = {}) {
      const drawer = document.querySelector('wubs-quick-add-drawer');
      if (drawer && typeof drawer.visaKonfig === 'function') {
        drawer.visaKonfig(opts);
        return;
      }
      if (!opts.handle) return;
      const root = window.Shopify?.routes?.root || '/';
      const v = opts.variantId ? `?variant=${encodeURIComponent(opts.variantId)}` : '';
      window.open(`${root}products/${encodeURIComponent(opts.handle)}${v}`, '_blank', 'noopener');
    },
  };

  /* Varan lades i korgen → stäng quick add så varukorgsdrawern (som wubs-product.js
     öppnar) inte hamnar bakom den. Händelsen skickas av wubs-product.js när korgen sagt ja.
     ⚠️ Förut stängdes lådan 60 ms efter submit, före svaret: säger korgen nej ska panelen stå
     kvar, annars försvinner beskedet vid knappen tillsammans med den (2026-11-03). */
  document.addEventListener('wubs:kop:klar', (e) => {
    if (!e.target.closest('[data-wubs-quickadd]')) return;
    document.querySelector('wubs-quick-add-drawer')?.close();
  });
})();
